import {
  EDITOR_FIELD_NAME_OF_DECEASED,
  canonicalizeEditorBlockText,
  mergeEditorBlockStyling,
} from '@messenger/lotus'
import type {
  EditorCharacterStylingInput,
  EditorTextBlockInput,
  EditorTextRunInput,
} from '@messenger/lotus/row-store'
import {
  tinyMceCaseDetailTarget,
  tinyMceTemplateTarget,
  type TinyMceFieldTarget,
} from './fieldKeys'
import {
  autoHiddenFieldSpaced,
  holdsLineOpen,
  isAutoHiddenField,
  tinyMceText,
} from './parse'
import type { EditorTextBoxConversionReportCollector } from './report'
import {
  TINYMCE_ROOT_CONTEXT,
  blockStylingOrNothing,
  fontSizeOf,
  foldCharacterStyling,
  stylingKey,
  tinyMceContextInside,
  unanimousLeafStyling,
  unanimousParagraphBlockStyling,
  type TinyMceContext,
} from './styleFold'
import { tinyMceCaseDetailTableBlock, tinyMceColumnTableBlock } from './tables'

type Report = EditorTextBoxConversionReportCollector
type FieldTarget = Extract<TinyMceFieldTarget, { kind: 'field' }>

/** Elements that sit inside a line; every other element lays out as a block. */
const INLINE_TAGS: ReadonlySet<string> = new Set([
  'A',
  'ABBR',
  'B',
  'BDI',
  'BDO',
  'BIG',
  'BR',
  'CITE',
  'CODE',
  'DEL',
  'DFN',
  'EM',
  'FONT',
  'I',
  'IMG',
  'INS',
  'KBD',
  'LABEL',
  'MARK',
  'Q',
  'S',
  'SAMP',
  'SMALL',
  'SPAN',
  'STRIKE',
  'STRONG',
  'SUB',
  'SUP',
  'TIME',
  'TT',
  'U',
  'VAR',
  'WBR',
])

type Piece =
  | { kind: 'text'; raw: string; styling: EditorCharacterStylingInput }
  | { kind: 'run'; run: EditorTextRunInput }

/**
 * One line of a paragraph as collected: its pieces, the styling at its end
 * (an empty line's size and colour), and whether its stored text holds the
 * line open when nothing of it converts.
 */
type Line = {
  pieces: Piece[]
  styling: EditorCharacterStylingInput
  holdsOpen: boolean
}

type LineSink = {
  lines: Line[]
  text: (raw: string, styling: EditorCharacterStylingInput) => void
  run: (run: EditorTextRunInput) => void
  lineBreak: (styling: EditorCharacterStylingInput) => void
}

const lineSink = (styling: EditorCharacterStylingInput): LineSink => {
  const lines: Line[] = [{ pieces: [], styling, holdsOpen: false }]
  const current = (): Line => lines[lines.length - 1]
  return {
    lines,
    text: (raw, textStyling) => {
      const line = current()
      line.pieces.push({ kind: 'text', raw, styling: textStyling })
      line.styling = textStyling
      if (holdsLineOpen(raw)) line.holdsOpen = true
    },
    run: (run) => current().pieces.push({ kind: 'run', run }),
    lineBreak: (breakStyling) => {
      current().styling = breakStyling
      lines.push({ pieces: [], styling: breakStyling, holdsOpen: false })
    },
  }
}

/**
 * A field's run, in the style its stored value painted in. A name whose
 * parts were styled apart becomes one run in the wrapper's style, which the
 * report names.
 */
const fieldRun = (
  target: FieldTarget,
  el: Element,
  styling: EditorCharacterStylingInput,
  report: Report,
): EditorTextRunInput => {
  const painted = unanimousLeafStyling(el, styling)
  if (!painted.unanimous && target.fieldKey === EDITOR_FIELD_NAME_OF_DECEASED)
    report.add({ kind: 'name-styled-as-one-run' })
  return {
    kind: 'field',
    fieldKey: target.fieldKey,
    ...(target.caseServiceId === undefined
      ? {}
      : { caseServiceId: target.caseServiceId }),
    ...(target.params === undefined ? {} : { params: target.params }),
    styling: painted.styling,
  }
}

/**
 * The field an inline element stands for, or undefined for ordinary markup.
 * A key with no scalar or composite home is reported, and its stored text is
 * kept where it shows, since the element's subtree is what TinyMCE painted
 * for it; where TinyMCE hid it, it converts to nothing and is still reported.
 */
const inlineFieldTarget = (
  el: Element,
  report: Report,
): FieldTarget | 'frozen' | undefined => {
  const tableKey = el.getAttribute('data-case-detail-table')
  const caseDetail = el.getAttribute('data-case-detail')
  const target =
    caseDetail !== null
      ? tinyMceCaseDetailTarget(caseDetail)
      : tableKey !== null
        ? ({ kind: 'frozen', key: tableKey } as const)
        : tinyMceTemplateTarget(el)
  if (target === undefined) return undefined
  if (target.kind === 'field') return target
  report.add({
    kind: 'frozen-field',
    key:
      target.kind === 'frozen' ? target.key : (caseDetail ?? target.fieldKey),
    count: 1,
  })
  return 'frozen'
}

/**
 * Collects inline content into lines: text as it stands, a field element as
 * one run whose stored value is never walked, a `<br>` as the end of a line.
 * Flattened (a frozen table cell), breaks and blocks are spaces instead.
 */
const collectNodes = (
  nodes: Iterable<Node>,
  styling: EditorCharacterStylingInput,
  sink: LineSink,
  report: Report,
  flatten: boolean,
): void => {
  for (const node of nodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      sink.text(node.nodeValue ?? '', styling)
      continue
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue
    const el = node as Element
    if (el.tagName === 'BR') {
      if (flatten) sink.text(' ', styling)
      else sink.lineBreak(styling)
      continue
    }
    const folded = foldCharacterStyling(styling, el)
    const target = inlineFieldTarget(el, report)
    // A hidden field is kept only to stay live; one that freezes would paint
    // stored text TinyMCE never showed, so it keeps its report entry alone.
    if (target === 'frozen' && isAutoHiddenField(el)) continue
    if (target !== undefined && target !== 'frozen') {
      const autoHidden = isAutoHiddenField(el)
      if (autoHidden && autoHiddenFieldSpaced(el, 'before'))
        sink.text(' ', styling)
      sink.run(fieldRun(target, el, folded, report))
      if (autoHidden && autoHiddenFieldSpaced(el, 'after'))
        sink.text(' ', styling)
      continue
    }
    const block = flatten && !INLINE_TAGS.has(el.tagName)
    if (block) sink.text(' ', styling)
    collectNodes(el.childNodes, folded, sink, report, flatten)
    if (block) sink.text(' ', styling)
  }
}

const mergedTextRuns = (runs: EditorTextRunInput[]): EditorTextRunInput[] =>
  runs.reduce<EditorTextRunInput[]>((merged, run) => {
    const prior = merged[merged.length - 1]
    if (
      run.kind === 'text' &&
      prior?.kind === 'text' &&
      stylingKey(prior.styling) === stylingKey(run.styling)
    )
      merged[merged.length - 1] = {
        ...prior,
        characters: prior.characters + run.characters,
      }
    else merged.push(run)
    return merged
  }, [])

/**
 * A line's runs. Stored text sheds TinyMCE's artifact characters, so the
 * hair-space glue TinyMCE puts around every field converts to nothing: two
 * fields joined by nothing but glue are adjacent runs, as fields inserted
 * back to back are, and the engine's field spacing decides how they paint. A
 * real space between them stays. Neighbouring runs of one styling merge, and
 * the model's canon folds the spaces.
 */
const lineRuns = (pieces: readonly Piece[]): EditorTextRunInput[] => {
  const runs = pieces.flatMap((piece): EditorTextRunInput[] => {
    if (piece.kind === 'run') return [piece.run]
    const characters = tinyMceText(piece.raw)
    return characters === ''
      ? []
      : [{ kind: 'text', characters, styling: piece.styling }]
  })
  return canonicalizeEditorBlockText({
    kind: 'paragraph',
    runs: mergedTextRuns(runs),
  }).runs
}

/**
 * A paragraph's lines as blocks, each with the paragraph's block styling. A
 * line a `<br>` ended is a block even when empty; the last line is dropped
 * when nothing of it converted and nothing in it held the line open, which
 * is how a trailing `<br>` and a paragraph of plain spaces paint nothing. An
 * empty block carries the styling at its end, the size its empty line had.
 */
const linesAsBlocks = (
  lines: readonly Line[],
  context: TinyMceContext,
): EditorTextBlockInput[] => {
  const blockStyling = blockStylingOrNothing(context.block)
  return lines.flatMap((line, index): EditorTextBlockInput[] => {
    const runs = lineRuns(line.pieces)
    if (runs.length === 0 && index === lines.length - 1 && !line.holdsOpen)
      return []
    return [
      {
        kind: 'paragraph',
        ...(blockStyling === undefined ? {} : { blockStyling }),
        ...(runs.length === 0 ? { characterStyling: line.styling } : {}),
        runs,
      },
    ]
  })
}

const paragraphBlocks = (
  nodes: Iterable<Node>,
  context: TinyMceContext,
  report: Report,
): EditorTextBlockInput[] => {
  const sink = lineSink(context.character)
  collectNodes(nodes, context.character, sink, report, false)
  return linesAsBlocks(sink.lines, context)
}

/**
 * A block-level field or template element as one block holding its field's
 * run; its stored paragraphs are the value the field resolves, so none of
 * them is kept. The paragraphs lend the block their alignment when they all
 * painted in one.
 */
const fieldBlock = (
  el: Element,
  target: FieldTarget,
  context: TinyMceContext,
  report: Report,
): EditorTextBlockInput => {
  const inside = tinyMceContextInside(context, el)
  const inner = unanimousParagraphBlockStyling(el, fontSizeOf(inside.character))
  const blockStyling = blockStylingOrNothing(
    inner === undefined
      ? inside.block
      : mergeEditorBlockStyling(inside.block, inner),
  )
  return {
    kind: 'paragraph',
    ...(blockStyling === undefined ? {} : { blockStyling }),
    runs: [fieldRun(target, el, inside.character, report)],
  }
}

/** A horizontal rule, its leading and stroke from the text around it, as the editor inserts one. */
const ruleBlock = (context: TinyMceContext): EditorTextBlockInput => {
  const { fontSize, color } = context.character
  const blockStyling = blockStylingOrNothing(context.block)
  return {
    kind: 'rule',
    ...(blockStyling === undefined ? {} : { blockStyling }),
    characterStyling: {
      ...(fontSize === undefined ? {} : { fontSize }),
      ...(color === undefined ? {} : { color }),
    },
    runs: [],
  }
}

const rowsOf = (table: Element): Element[] =>
  [...table.children].flatMap((child) =>
    child.tagName === 'TR'
      ? [child]
      : ['TBODY', 'THEAD', 'TFOOT'].includes(child.tagName)
        ? [...child.children].filter((row) => row.tagName === 'TR')
        : [],
  )

/**
 * A table no case field claims, frozen as one paragraph per row, its cells
 * joined by a space; a field inside a cell stays live.
 */
const literalRowBlocks = (
  table: Element,
  context: TinyMceContext,
  report: Report,
): EditorTextBlockInput[] => {
  const tableContext = tinyMceContextInside(context, table)
  return rowsOf(table).flatMap((row) => {
    const rowContext = tinyMceContextInside(tableContext, row)
    const sink = lineSink(rowContext.character)
    ;[...row.children]
      .filter((cell) => cell.tagName === 'TD' || cell.tagName === 'TH')
      .forEach((cell, i) => {
        if (i > 0) sink.text(' ', rowContext.character)
        collectNodes(
          cell.childNodes,
          foldCharacterStyling(rowContext.character, cell),
          sink,
          report,
          true,
        )
      })
    const runs = lineRuns(sink.lines[0].pieces)
    const blockStyling = blockStylingOrNothing(tableContext.block)
    return runs.length === 0
      ? []
      : [
          {
            kind: 'paragraph' as const,
            ...(blockStyling === undefined ? {} : { blockStyling }),
            runs,
          },
        ]
  })
}

/** A block-level element as the blocks it converts to. */
const elementBlocks = (
  el: Element,
  context: TinyMceContext,
  report: Report,
): EditorTextBlockInput[] => {
  const tableKey = el.getAttribute('data-case-detail-table')
  if (tableKey !== null) {
    const target = tinyMceCaseDetailTarget(tableKey)
    if (target.kind === 'tabular')
      return [tinyMceCaseDetailTableBlock(el, target.fieldKey, context)]
    report.add({ kind: 'frozen-field', key: tableKey, count: 1 })
    const table = el.querySelector('table')
    return table === null || isAutoHiddenField(el)
      ? []
      : literalRowBlocks(table, tinyMceContextInside(context, el), report)
  }
  const caseDetail = el.getAttribute('data-case-detail')
  const target =
    caseDetail === null
      ? tinyMceTemplateTarget(el)
      : tinyMceCaseDetailTarget(caseDetail)
  if (target?.kind === 'field') return [fieldBlock(el, target, context, report)]
  if (target?.kind === 'tabular')
    return [tinyMceColumnTableBlock(el, target.fieldKey, context, report)]
  if (target?.kind === 'frozen') {
    report.add({ kind: 'frozen-field', key: target.key, count: 1 })
    if (isAutoHiddenField(el)) return []
  }
  if (el.tagName === 'P')
    return paragraphBlocks(
      el.childNodes,
      tinyMceContextInside(context, el),
      report,
    )
  if (el.tagName === 'HR') return [ruleBlock(context)]
  if (el.tagName === 'TABLE') {
    report.add({ kind: 'table-frozen', count: 1 })
    return literalRowBlocks(el, context, report)
  }
  return containerBlocks(el, tinyMceContextInside(context, el), report)
}

/**
 * A container's children as blocks: each block-level child as its own
 * blocks, and the inline content between them (bare text, styled spans,
 * inline fields) grouped into a paragraph, as the browser lays it out.
 */
const containerBlocks = (
  container: Element,
  context: TinyMceContext,
  report: Report,
): EditorTextBlockInput[] => {
  const blocks: EditorTextBlockInput[] = []
  let inline: Node[] = []
  const flush = (): void => {
    if (inline.length > 0)
      blocks.push(...paragraphBlocks(inline, context, report))
    inline = []
  }
  for (const child of container.childNodes) {
    const isInline =
      child.nodeType === Node.TEXT_NODE ||
      (child.nodeType === Node.ELEMENT_NODE &&
        INLINE_TAGS.has((child as Element).tagName))
    if (isInline) {
      inline.push(child)
      continue
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue
    flush()
    blocks.push(...elementBlocks(child as Element, context, report))
  }
  flush()
  return blocks
}

/** The blocks a TinyMCE item's parsed HTML converts to, from TinyMCE's root styling down. */
export const tinyMceBlocks = (
  body: HTMLElement,
  report: Report,
): EditorTextBlockInput[] => containerBlocks(body, TINYMCE_ROOT_CONTEXT, report)
